import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import DarkLayout from './DarkLayout';
import { HeaderPrimaryAddButton } from './HeaderPrimaryAddButton';
import { ResponsiveToolbarButton } from './ResponsiveToolbarButton';
import ProjectFormDialog from './ProjectFormDialog';
import NewEstimateDialog from './NewEstimateDialog';
import {
  ListPageTableSurface,
  ListPageMobileStack,
  ListPageTableHeadRow,
  ListPageTh,
  listPageTableBodyClass,
  listPageTableRowClass,
} from './listPage';
import { apiService } from '../services/api';
import { Project as ProjectType, EstimateList } from '../types/api';
import { formatCurrencyCompact } from '../utils/formatCurrency';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions } from '../constants/permissions';

const PAGE_SIZE = 10;

function formatMoney(v?: number): string {
  if (v == null) return '—';
  return formatCurrencyCompact(v);
}

function formatDate(s?: string): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(s?: string): string {
  if (!s) return '—';
  const d = new Date(s);
  const date = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `${date} • ${time}`;
}

function getStatusBadgeClass(status: string): string {
  const s = (status || '').trim();
  switch (s) {
    case 'Draft':
      return 'bg-amber-500/10 border border-amber-500/30 text-amber-400';
    case 'Proposed':
      return 'bg-amber-500/10 border border-amber-500/30 text-amber-400';
    case 'Submitted':
      return 'bg-blue-500/10 border border-blue-500/30 text-blue-400';
    case 'Approved':
      return 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400';
    default:
      return 'bg-slate-700/50 border border-slate-600/50 text-slate-300';
  }
}

function getProjectStatusLabel(status: string): string {
  switch (status) {
    case 'Active': return 'In Progress';
    case 'Draft': return 'Proposal';
    case 'OnHold': return 'On Hold';
    case 'Completed': return 'Completed';
    default: return status;
  }
}

function getProjectStatusBadgeClass(status: string): string {
  switch (status) {
    case 'Active':
      return 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400';
    case 'Draft':
      return 'bg-amber-500/10 border border-amber-500/30 text-amber-400';
    case 'OnHold':
      return 'bg-slate-700/50 border border-slate-600/50 text-slate-300';
    case 'Completed':
      return 'bg-blue-500/10 border border-blue-500/30 text-blue-400';
    default:
      return 'bg-slate-700/50 border border-slate-600/50 text-slate-300';
  }
}

function totalWithContingencyAndTax(
  subtotal: number | undefined,
  contingencyPercent?: number,
  taxPercent?: number
): number {
  const base = subtotal ?? 0;
  const contingencyPct = contingencyPercent ?? 10;
  const taxPct = taxPercent ?? 0;
  const contingency = base * (contingencyPct / 100);
  const withContingency = base + contingency;
  const tax = withContingency * (taxPct / 100);
  return withContingency + tax;
}

const ProjectEstimates: React.FC = () => {
  const { user } = useAuth();
  const canCreateEstimate = hasPermission(user, permissions.estimates.create);
  const canEditProject = hasPermission(user, permissions.projects.edit);
  const canEditEstimate = hasPermission(user, permissions.estimates.edit);
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectType | null>(null);
  const [estimates, setEstimates] = useState<EstimateList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [page, setPage] = useState(1);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newEstimateDialogOpen, setNewEstimateDialogOpen] = useState(false);

  useDocumentTitle(
    project?.name?.trim() ? `${project.name.trim()} · Estimates` : 'Estimates'
  );

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [proj, list] = await Promise.all([
          apiService.getProject(projectId),
          apiService.getProjectEstimates(projectId),
        ]);
        if (!cancelled) {
          setProject(proj);
          setEstimates(list);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Failed to load project or estimates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const reloadProject = async () => {
    if (!projectId) return;
    try {
      const [proj, list] = await Promise.all([
        apiService.getProject(projectId),
        apiService.getProjectEstimates(projectId),
      ]);
      setProject(proj);
      setEstimates(list);
    } catch (err) {
      console.error(err);
    }
  };

  const sortedEstimates = useMemo(() => {
    const list = [...estimates];
    list.sort((a, b) => {
      const ta = new Date(a.createdAt).getTime();
      const tb = new Date(b.createdAt).getTime();
      return sortOrder === 'desc' ? tb - ta : ta - tb;
    });
    return list;
  }, [estimates, sortOrder]);

  const totalCount = sortedEstimates.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const end = Math.min(start + PAGE_SIZE, totalCount);
  const pagedEstimates = useMemo(
    () => sortedEstimates.slice(start, end),
    [sortedEstimates, start, end]
  );

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  if (!projectId) {
    navigate('/projects');
    return null;
  }

  return (
    <DarkLayout
      title="Project Overview"
      activeTab="projects"
      headerRight={
        canCreateEstimate ? (
          <HeaderPrimaryAddButton
            label="Create New Estimates"
            onClick={() => setNewEstimateDialogOpen(true)}
            mobileAriaLabel="Create new estimate"
          />
        ) : null
      }
    >
      <div className="p-4 md:p-5 flex flex-col gap-5 pb-32 lg:pb-5">
        <ResponsiveToolbarButton
          icon="arrow_back"
          label="Back to Projects"
          variant="primary"
          onClick={() => navigate('/projects')}
          mobileAriaLabel="Back to projects list"
          className="self-start"
        />

        {error && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/20 px-4 py-2 text-red-400 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-6">
            <div className="bg-card-dark border border-border-dark rounded-2xl p-5 md:p-5 h-48 animate-pulse" />
            <div className="h-8 w-48 bg-card-dark rounded" />
            <div className="h-64 bg-card-dark rounded-2xl animate-pulse" />
          </div>
        ) : project ? (
          <>
            {/* Project Name card */}
            <section>
              <div className="bg-card-dark border border-border-dark rounded-2xl p-5 md:p-5 shadow-sm relative overflow-hidden">
                <div className="relative z-10">
                  <div className="flex flex-col gap-5">
                    <div className="space-y-4">
                      <div>
                        <span className="text-[10px] font-bold text-primary uppercase tracking-[0.2em]">
                          Project Name
                        </span>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mt-2 sm:mt-1">
                          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
                            {project.name}
                          </h2>
                          {canEditProject && (
                            <button
                              type="button"
                              onClick={() => setEditDialogOpen(true)}
                              className="w-fit px-3 py-1.5 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg transition-colors flex items-center gap-2"
                            >
                              <span className="material-symbols-outlined text-lg leading-none">edit_note</span>
                              <span className="text-[10px] font-bold uppercase tracking-wider">Edit Details</span>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-5">
                        <div>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-bold tracking-wider mb-1">
                            Status
                          </p>
                          <span
                            className={`inline-flex items-center px-3 py-1 text-[10px] font-black uppercase tracking-wider rounded-full ${getProjectStatusBadgeClass(project.status)}`}
                          >
                            {getProjectStatusLabel(project.status)}
                          </span>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-bold tracking-wider mb-1">
                            Client
                          </p>
                          <p className="text-sm md:text-base font-semibold text-slate-200 truncate">
                            {project.clientName ?? '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-bold tracking-wider mb-1">
                            Site Address
                          </p>
                          <p className="text-sm md:text-base font-medium text-slate-400">
                            {project.siteAddress ?? '—'}
                          </p>
                        </div>
                        <div className="col-span-2 md:col-span-1">
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-bold tracking-wider mb-1">
                            Overall Budget
                          </p>
                          <p className="text-xl md:text-2xl font-bold text-emerald-500">
                            {formatMoney(project.budget)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -right-8 -bottom-8 opacity-[0.03] dark:opacity-[0.07] pointer-events-none">
                  <span className="material-symbols-outlined text-[160px] md:text-[240px] text-primary">architecture</span>
                </div>
              </div>
            </section>

            {/* Estimates History */}
            <section className="space-y-4">
              <div className="flex flex-row items-center justify-between gap-4 px-0">
                <h3 className="text-[10px] md:text-sm font-bold text-slate-500 uppercase tracking-widest">
                  Proposed Estimates ({totalCount})
                </h3>
                <ResponsiveToolbarButton
                  icon="filter_list"
                  label={`Sort by Date ${sortOrder === 'desc' ? '(Newest)' : '(Oldest)'}`}
                  onClick={() => setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}
                  mobileAriaLabel={
                    sortOrder === 'desc'
                      ? 'Sort by date, newest first. Tap to show oldest first.'
                      : 'Sort by date, oldest first. Tap to show newest first.'
                  }
                  className="!text-[10px] md:!text-sm !font-bold !text-primary !border-primary/30 hover:!bg-primary/10 !rounded-lg"
                />
              </div>

              {totalCount === 0 ? (
                <div className="bg-elevation-dark rounded-xl border border-border-dark p-5 text-center text-slate-400 shadow-2xl overflow-hidden">
                  <p className="font-medium">No estimates yet.</p>
                  <p className="text-sm mt-1">Create one to get started.</p>
                  {canCreateEstimate && (
                    <button
                      type="button"
                      onClick={() => setNewEstimateDialogOpen(true)}
                      className="mt-4 text-primary font-semibold hover:underline"
                    >
                      Create New Estimates
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {/* Desktop table - match Projects list styling */}
                  <ListPageTableSurface breakpoint="lg">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <ListPageTableHeadRow>
                          <ListPageTh>Estimate Name / Version</ListPageTh>
                          <ListPageTh>Last Modified Date</ListPageTh>
                          <ListPageTh className="text-center">Status</ListPageTh>
                          <ListPageTh className="text-right">Total Estimate</ListPageTh>
                          <ListPageTh className="text-right">Actions</ListPageTh>
                        </ListPageTableHeadRow>
                      </thead>
                      <tbody className={listPageTableBodyClass}>
                        {pagedEstimates.map((e) => (
                          <tr key={e.estimateId} className={listPageTableRowClass}>
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors shrink-0">
                                  <span className="material-symbols-outlined text-xl">description</span>
                                </div>
                                <div>
                                  <p className="font-semibold text-sm text-white">
                                    {e.title || `Estimate #${e.estimateId}`}
                                  </p>
                                  <p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">
                                    Estimate • {formatDate(e.updatedAt ?? e.createdAt)}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-4">
                              <p className="text-slate-300 text-sm">
                                {formatDate(e.updatedAt ?? e.createdAt)}
                              </p>
                              <p className="text-[10px] text-slate-500 font-bold uppercase mt-0.5">
                                {(e.updatedAt ?? e.createdAt)
                                  ? new Date(e.updatedAt ?? e.createdAt).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: true,
                                    })
                                  : '—'}
                              </p>
                            </td>
                            <td className="px-5 py-4 text-center">
                              <span
                                className={`inline-flex items-center px-3 py-1 text-[10px] font-bold rounded-full border uppercase tracking-wider ${getStatusBadgeClass(e.status)}`}
                              >
                                {e.status}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-right">
                              <p className="text-white font-bold text-sm">
                                {formatMoney(
                                  totalWithContingencyAndTax(
                                    e.totalAmount,
                                    e.contingencyPercent,
                                    e.taxPercent
                                  )
                                )}
                              </p>
                            </td>
                            <td className="px-5 py-4 text-right">
                              <div className="action-row-icon-buttons">
                                <button
                                  type="button"
                                  title="Export PDF (coming soon)"
                                  disabled
                                  className="text-slate-500 opacity-60 cursor-not-allowed hover:bg-white/5"
                                >
                                  <span className="material-symbols-outlined">picture_as_pdf</span>
                                </button>
                                {canEditEstimate && (
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/projects/${projectId}/estimates/${e.estimateId}`)}
                                    className="text-primary hover:bg-primary/10"
                                    aria-label="Open estimate"
                                  >
                                    <span className="material-symbols-outlined">edit</span>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ListPageTableSurface>

                  {/* Mobile/tablet cards - match Projects card list styling */}
                  <ListPageMobileStack breakpoint="lg">
                    {pagedEstimates.map((e) => (
                      <div
                        key={e.estimateId}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/projects/${projectId}/estimates/${e.estimateId}`)}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            navigate(`/projects/${projectId}/estimates/${e.estimateId}`);
                          }
                        }}
                        className="bg-elevation-dark rounded-xl p-4 border border-border-dark shadow-lg flex flex-col gap-4 cursor-pointer hover:border-primary/30 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex items-start gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                              <span className="material-symbols-outlined">description</span>
                            </div>
                            <div className="space-y-0.5 min-w-0">
                              <p className="font-bold text-lg leading-tight text-white truncate">
                                {e.title || `Estimate #${e.estimateId}`}
                              </p>
                              <p className="text-slate-400 text-sm">
                                Estimate • {formatDate(e.updatedAt ?? e.createdAt)}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`inline-flex items-center px-3 py-1 text-[10px] font-bold rounded-full border uppercase tracking-wider shrink-0 ${getStatusBadgeClass(e.status)}`}
                          >
                            {e.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-700/50">
                          <div>
                            <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">Last Modified</p>
                            <p className="text-slate-300 text-sm font-medium">
                              {formatDateTime(e.updatedAt ?? e.createdAt)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">Total Estimate</p>
                            <p className="text-white font-bold text-sm">
                              {formatMoney(
                                totalWithContingencyAndTax(
                                  e.totalAmount,
                                  e.contingencyPercent,
                                  e.taxPercent
                                )
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </ListPageMobileStack>

                  {/* Pagination */}
                  {totalCount > 0 && (
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 px-0 pt-2 md:pt-4">
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                        Showing {totalCount === 0 ? 0 : start + 1}-{end} of {totalCount} Estimates
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page <= 1}
                          className="w-8 h-8 flex items-center justify-center rounded border border-border-dark hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed text-slate-300 bg-elevation-dark"
                        >
                          <span className="material-symbols-outlined text-sm">chevron_left</span>
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setPage(p)}
                            className={`w-8 h-8 flex items-center justify-center rounded border font-bold text-xs transition-colors ${
                              p === page
                                ? 'bg-primary text-white border-primary'
                                : 'border-border-dark hover:bg-white/5 text-slate-300 bg-elevation-dark'
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page >= totalPages}
                          className="w-8 h-8 flex items-center justify-center rounded border border-border-dark hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed text-slate-300 bg-elevation-dark"
                        >
                          <span className="material-symbols-outlined text-sm">chevron_right</span>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        ) : null}
      </div>

      <ProjectFormDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        project={project}
        onSuccess={reloadProject}
      />
      <NewEstimateDialog
        open={newEstimateDialogOpen}
        onClose={() => setNewEstimateDialogOpen(false)}
        projectId={projectId}
        onSuccess={(estimateId) => {
          reloadProject();
          navigate(`/projects/${projectId}/estimates/${estimateId}`);
        }}
      />
    </DarkLayout>
  );
};

export default ProjectEstimates;
